import moment from "moment";
import React, { ReactElement, useEffect, useState } from "react";
import { FormControl } from "react-bootstrap";
import { useHistory, useParams } from "react-router";
import { DragDropContext, DropResult } from "@hello-pangea/dnd";
import {
	getColorForTaskStatus,
	Project,
	ProjectTask,
	ProjectTaskModal,
	ProjectTaskStatus,
	useProjects,
} from ".";
import { Loading } from "../ui";
import { ProjectTaskCard } from "./ProjectTaskCard";
import { KanbanLane, KanbanLaneData } from "./KanbanLane";
import { KanbanCard } from "./KanbanCard";

interface IProps {
	project: Project;
}

interface CardData {
	id: string;
	title: string;
	label: string;
	task: ProjectTask;
}

interface LaneData extends KanbanLaneData {
	cards: CardData[];
}

export const ProjectBoard = (props: IProps): ReactElement => {
	const [shownTask, setShownTask] = useState<ProjectTask | null>();
	const [searchTerm, setSearchTerm] = useState<string>();
	const [localTasks, setLocalTasks] = useState<ProjectTask[]>([]);

	const projectContext = useProjects();
	const { taskId } = useParams<any>();
	const history = useHistory();

	// Sync local tasks with context tasks
	useEffect(() => {
		if (projectContext.tasks) {
			setLocalTasks([...projectContext.tasks]);
		}
	}, [projectContext.tasks]);

	if (!props.project) {
		return <strong>"Invalid Project"</strong>;
	}

	const formatTaskForBoard = (tasksData: ProjectTask[]): LaneData[] => {
		const lanes: LaneData[] = Object.values(ProjectTaskStatus).map(
			(status, index) => ({
				id: `lane${index}`,
				title: status,
				color: getColorForTaskStatus(status),
				cards: [],
			}),
		);

		tasksData.forEach((task: ProjectTask): void => {
			const lane = lanes.find((l) => l.title === task.status);

			if (lane) {
				lane.cards.push({
					id: String(task.id),
					title: task.name,
					label: moment(task.createdDate).fromNow(true),
					task,
				});
			}
		});

		return lanes;
	};

	const fetchProjectTasks = async (term?: string) => {
		await projectContext.findTasks!(term);
	};

	useEffect(() => {
		fetchProjectTasks(searchTerm);
	}, [searchTerm]);

	useEffect(() => {
		if (taskId) {
			if (localTasks.length > 0) {
				const tid = parseInt(taskId, 10);
				const openTask = localTasks.filter((t) => t.id === tid);
				if (openTask[0]) {
					setShownTask(openTask[0]);
				}
			}
		}
	}, [localTasks, taskId]);

	const handleDragEnd = (result: DropResult) => {
		const { destination, source, draggableId } = result;

		// Dropped outside a droppable area
		if (!destination) {
			return;
		}

		// Dropped in the same position
		if (
			destination.droppableId === source.droppableId &&
			destination.index === source.index
		) {
			return;
		}

		const lanes = formatTaskForBoard(localTasks);
		const targetLane = lanes.find((l) => l.id === destination.droppableId);

		if (!targetLane) {
			return;
		}

		// Optimistic update: Update local state immediately
		const taskId = parseInt(draggableId, 10);
		const updatedTasks = localTasks.map((task) => {
			if (task.id === taskId) {
				// Create a new task object with updated status
				const updatedTask = new ProjectTask({
					...task,
					status: targetLane.title,
					position: destination.index,
				});
				// Copy over all properties
				Object.assign(updatedTask, task);
				updatedTask.status = targetLane.title;
				updatedTask.position = destination.index;
				return updatedTask;
			}
			return task;
		});

		setLocalTasks(updatedTasks);

		// Then sync with backend (this will eventually update projectContext.tasks)
		projectContext.changeTaskStatus!(
			taskId,
			destination.index,
			targetLane.title,
		);
	};

	const handleAddCard = (laneId: string, title: string, description: string) => {
		const lanes = formatTaskForBoard(localTasks);
		const lane = lanes.find((l) => l.id === laneId);

		if (lane) {
			projectContext.createTask!(title, description, lane.title);
		}
	};

	const handleCardClick = (task: ProjectTask) => {
		setShownTask(task);
	};

	if (!projectContext.tasks) {
		return <Loading />;
	}

	const lanes = formatTaskForBoard(localTasks);

	let taskModal = null;

	if (shownTask) {
		taskModal = (
			<ProjectTaskModal
				task={shownTask}
				show={true}
				onHide={() => {
					setShownTask(null);
					history.push(`/projects/${projectContext.project!.id}`);
				}}
			/>
		);
	}

	return (
		<>
			{taskModal}
			<FormControl
				style={{ marginLeft: 20, marginBottom: 10, maxWidth: 300 }}
				placeholder="Filter tasks..."
				onChange={(e: any) => setSearchTerm(e.target.value)}
			/>
			<DragDropContext onDragEnd={handleDragEnd}>
				<div className="kanban-board">
					{lanes.map((lane) => (
						<KanbanLane
							key={lane.id}
							lane={lane}
							onAddCard={handleAddCard}
						>
							{lane.cards.map((card, index) => (
								<KanbanCard key={card.id} id={card.id} index={index}>
									<ProjectTaskCard
										task={card.task}
										onClick={() => handleCardClick(card.task)}
									/>
								</KanbanCard>
							))}
						</KanbanLane>
					))}
				</div>
			</DragDropContext>
		</>
	);
};
